import React, { useState } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import {
  CardEntityType,
  ICustomCardEntityConfig,
  resolveCustomCardEntityConfig,
  ICustomCardConfig,
  DEFAULT_CUSTOM_CARD_CONFIG,
} from "common247";
import { navigateToCardEntity } from "./card-helpers";
import { renderCustomCardField } from "./custom-card-fields";
import { renderCustomCardSection } from "./custom-card-sections";

export interface IThemedCustomCardProps {
  /** Entity domain category */
  entityType: CardEntityType;
  /** Primary identifier */
  id?: string;
  /** Complete or partial entity object (optional if individual props are passed) */
  item?: Record<string, any>;
  /** Primary title (e.g. Memory title, Media name, Contact name) */
  title?: string;
  /** Formatted date string */
  formattedDate?: string;
  /** Subtitle or date text */
  dateText?: string;
  /** Entity description */
  description?: string;
  /** Email address */
  email?: string;
  /** Image thumbnail URL */
  thumbnailUrl?: string;
  /** Owner display name */
  ownerDisplayName?: string;
  /** Recipient display name (for shares) */
  recipientDisplayName?: string;
  /** Role string */
  role?: string;
  /** Associated media items */
  mediaList?: any[];
  /** Associated contacts */
  contactsList?: any[];
  /** Associated groups */
  groupsList?: any[];
  /** Associated memories */
  memoriesList?: any[];
  /** Associated tags */
  tagsList?: any[];
  /** Associated shares */
  sharesList?: any[];
  /** Optional custom card configuration for this entity (defaults to user config or Compact fallback) */
  config?: ICustomCardEntityConfig;
  /** User-wide CustomCard config map */
  userConfig?: ICustomCardConfig;
  /** Contextual kebab menu actions */
  kebabActions?: IKebabActionItem[];
  /** Whether the user is the owner */
  isOwner?: boolean;
  /** Whether the item is the logged-on AppUser / self-contact */
  isSelf?: boolean;
  /** Whether the entity has a 5-star rating */
  isFiveStar?: boolean;
  onPress?: () => void;
  onEditPress?: () => void;
  onDeletePress?: () => void;
  onSharePress?: () => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  [key: string]: any;
}

/**
 * ThemedCustomCard: Tier 5 Item Card component.
 *
 * Implements a dynamic, user-configured card layout driven by `ICustomCardEntityConfig`:
 * - Renders configured fields in the user's defined order
 * - Renders configured linked sections in the user's defined order:
 *   - Contacts, Groups, Memories, Tags, Shares: Rendered as `<ThemedChip>`
 *   - Media: Rendered as `<ThemedCompactCard entityType="media">`
 * - When unconfigured, automatically defaults to the "Compact" definition.
 */
export function ThemedCustomCard({
  entityType,
  id,
  item: rawItem,
  title: propTitle,
  formattedDate: propFormattedDate,
  dateText: propDateText,
  description: propDescription,
  email: propEmail,
  thumbnailUrl: propThumbnailUrl,
  ownerDisplayName: propOwnerDisplayName,
  recipientDisplayName: propRecipientDisplayName,
  role: propRole,
  mediaList: propMediaList,
  contactsList: propContactsList,
  groupsList: propGroupsList,
  memoriesList: propMemoriesList,
  tagsList: propTagsList,
  sharesList: propSharesList,
  config,
  userConfig,
  kebabActions,
  isOwner = false,
  isSelf = false,
  isFiveStar = false,
  onPress,
  onEditPress,
  onDeletePress,
  onSharePress,
  style,
  testID,
  ...restProps
}: IThemedCustomCardProps): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const { card: cardTheme } = useComponentTheme();
  const cardBg = cardTheme.chrome.backgroundColor;
  const borderColor = cardTheme.chrome.restingBorderColor;
  const tintColor = cardTheme.chrome.selectedBorderColor;
  const textColor = cardTheme.chrome.textColor;
  const textMuted = cardTheme.chrome.textMutedColor;

  // Construct a safe, merged item containing both rawItem and explicitly passed props
  const item: Record<string, any> = {
    ...restProps,
    ...(rawItem || {}),
  };
  if (id !== undefined) item.id = id;
  if (propTitle !== undefined) item.title = propTitle;
  if (propFormattedDate !== undefined) item.formattedDate = propFormattedDate;
  if (propDateText !== undefined) item.dateText = propDateText;
  if (propDescription !== undefined) item.description = propDescription;
  if (propEmail !== undefined) item.email = propEmail;
  if (propThumbnailUrl !== undefined) item.thumbnailUrl = propThumbnailUrl;
  if (propOwnerDisplayName !== undefined) item.ownerDisplayName = propOwnerDisplayName;
  if (propRecipientDisplayName !== undefined) item.recipientDisplayName = propRecipientDisplayName;
  if (propRole !== undefined) item.role = propRole;
  if (propMediaList !== undefined) item.mediaList = propMediaList;
  if (propContactsList !== undefined) item.contactsList = propContactsList;
  if (propGroupsList !== undefined) item.groupsList = propGroupsList;
  if (propMemoriesList !== undefined) item.memoriesList = propMemoriesList;
  if (propTagsList !== undefined) item.tagsList = propTagsList;
  if (propSharesList !== undefined) item.sharesList = propSharesList;
  if (isOwner !== undefined) item.isOwner = isOwner;
  if (isFiveStar !== undefined) item.isFiveStar = isFiveStar;

  // Resolve active custom card configuration (falls back to Compact default)
  const resolvedConfig = config ?? resolveCustomCardEntityConfig(userConfig, entityType);
  const rawFields = resolvedConfig?.fields;
  const rawSections = resolvedConfig?.sections;

  const fields: string[] = Array.from(
    new Set(
      Array.isArray(rawFields)
        ? rawFields
        : DEFAULT_CUSTOM_CARD_CONFIG[entityType]?.fields ?? ["title", "date"]
    )
  );

  const sections: string[] = Array.from(
    new Set(
      Array.isArray(rawSections)
        ? rawSections
        : DEFAULT_CUSTOM_CARD_CONFIG[entityType]?.sections ?? []
    )
  );

  const effectiveId = id || item.id;
  const title =
    propTitle || item.title || item.displayName || item.name || item.value || `Untitled ${entityType}`;
  const dateStr =
    propFormattedDate ||
    propDateText ||
    item.formattedDate ||
    item.dateText ||
    item.date ||
    item.createdAt
      ? String(propFormattedDate || propDateText || item.formattedDate || item.dateText || item.date || "")
      : "";
  const photoUrl =
    propThumbnailUrl || item.thumbnailUrl || item.photoIconUrl || item.photoUrl || item.downloadUrl || item.url;
  const description = propDescription || item.description || "";
  const email = propEmail || item.email || "";

  const handleCardPress = () => {
    if (onPress) {
      onPress();
      return;
    }
    navigateToCardEntity(entityType, effectiveId, "ThemedCustomCard");
  };

  // Build effective kebab actions if not passed explicitly but callback props exist
  const effectiveKebabActions: IKebabActionItem[] = [...(kebabActions || [])];
  if (effectiveKebabActions.length === 0) {
    if (onSharePress) {
      effectiveKebabActions.push({
        id: "share",
        label: "Share",
        icon: "share-social-outline",
        onPress: onSharePress,
      });
    }
    if (onEditPress) {
      effectiveKebabActions.push({
        id: "edit",
        label: "Edit",
        icon: "pencil-outline",
        onPress: onEditPress,
      });
    }
    if (onDeletePress) {
      effectiveKebabActions.push({
        id: "delete",
        label: "Delete",
        icon: "trash-outline",
        destructive: true,
        onPress: onDeletePress,
      });
    }
    if (effectiveKebabActions.length === 0 && effectiveId) {
      effectiveKebabActions.push({
        id: "view",
        label: `View ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`,
        icon: "eye-outline",
        onPress: handleCardPress,
      });
    }
  }

  let renderedLinksSection = false;

  return (
    <TouchableOpacity
      testID={testID}
      activeOpacity={0.85}
      onPress={handleCardPress}
      {...(Platform.OS === "web"
        ? ({
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          } as any)
        : {})}
      style={[
        styles.cardContainer,
        {
          backgroundColor: cardBg,
          borderColor: isHovered ? tintColor : borderColor,
        },
        style,
      ]}
      accessibilityRole={Platform.select({ web: "article" as any, default: undefined })}
      accessibilityLabel={`Custom card: ${title}`}
    >
      {/* Platform Symbols & Kebab Menu row */}
      <View style={styles.topRightSymbols}>
        {isSelf && <ThemedPlatformSymbol type="you" size="small" />}
        {isOwner && <ThemedPlatformSymbol type="owner" size="small" />}
        {isFiveStar && <ThemedPlatformSymbol type="fivestar" size="small" />}
        {effectiveKebabActions.length > 0 && (
          <ThemedKebabMenu
            actions={effectiveKebabActions}
            compact={true}
            accessibilityLabel={`Options for ${title}`}
          />
        )}
      </View>

      {/* Render Configured Fields */}
      <View style={styles.fieldsContainer}>
        {fields.map((fKey, idx) =>
          renderCustomCardField({
            fieldKey: fKey,
            idx,
            item,
            title,
            dateStr,
            photoUrl,
            description,
            email,
            textColor,
            textMuted,
          })
        )}
      </View>

      {/* Render Configured Sections */}
      {sections.length > 0 && (
        <View style={styles.sectionsContainer}>
          {sections.map((sKey, idx) =>
            renderCustomCardSection({
              sectionKey: sKey,
              idx,
              entityType,
              item,
              title,
              tintColor,
              textMuted,
              linksAlreadyRendered: renderedLinksSection,
              onMarkLinksRendered: () => {
                renderedLinksSection = true;
              },
            })
          )}
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    borderWidth: 1,
    borderRadius: 4,
    padding: 8,
    position: "relative",
    overflow: "visible",
    gap: 5,
  },
  topRightSymbols: {
    position: "absolute",
    top: 6,
    right: 6,
    flexDirection: "row",
    gap: 4,
    zIndex: 10,
    alignItems: "center",
    overflow: "visible",
  },
  fieldsContainer: {
    gap: 4,
    paddingRight: 64,
  },
  sectionsContainer: {
    marginTop: 3,
    gap: 4,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "rgba(150, 150, 150, 0.2)",
    paddingTop: 3,
  },
});

export default ThemedCustomCard;
